import React, { useState, useRef } from 'react';
import { Upload, X, RefreshCw, Image, Loader2 } from 'lucide-react';
import api from '../../utils/api';

const ImageUpload = ({ value, onChange, label = 'Image', endpoint = '/upload/image', fieldName = 'image' }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState('');
  const [preview, setPreview]     = useState(value || '');
  const inputRef = useRef();

  const handleFile = async (file) => {
    if (!file) return;
    setError('');
    const obj = URL.createObjectURL(file);
    setPreview(obj);
    const fd = new FormData();
    fd.append(fieldName, file);
    setUploading(true);
    try {
      const res = await api.post(endpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(res.data.url);
      onChange(res.data.url);
      URL.revokeObjectURL(obj);
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed');
      setPreview(value || '');
    } finally { setUploading(false); }
  };

  return (
    <div className="mb-3">
      {label && <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{label}</label>}
      <div
        onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
        onDragOver={e => e.preventDefault()}
        onClick={() => !uploading && inputRef.current?.click()}
        className={`relative rounded-xl border-2 border-dashed transition-all cursor-pointer overflow-hidden ${preview ? 'border-royal-300' : 'border-gray-200 hover:border-royal-400 bg-gray-50 hover:bg-royal-50'}`}
      >
        {preview ? (
          <div className="relative">
            <img src={preview} alt="Preview" className="w-full max-h-52 object-cover block" />
            {uploading && (
              <div className="absolute inset-0 bg-white/70 flex items-center justify-center gap-2">
                <Loader2 size={20} className="animate-spin text-royal-800" />
                <span className="text-sm font-medium text-royal-800">Compressing...</span>
              </div>
            )}
            {!uploading && (
              <div className="absolute inset-0 bg-black/0 hover:bg-black/40 transition-all flex items-center justify-center gap-3 opacity-0 hover:opacity-100">
                <button type="button" onClick={e => { e.stopPropagation(); inputRef.current?.click(); }}
                  className="flex items-center gap-1.5 px-3 py-2 bg-white rounded-lg text-xs font-semibold">
                  <RefreshCw size={13} /> Replace
                </button>
                <button type="button" onClick={e => { e.stopPropagation(); setPreview(''); onChange(''); }}
                  className="flex items-center gap-1.5 px-3 py-2 bg-red-500 text-white rounded-lg text-xs font-semibold">
                  <X size={13} /> Remove
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center py-8 gap-2 text-gray-400">
            {uploading
              ? <><Loader2 size={28} className="animate-spin text-royal-600 mb-1" /><span className="text-sm">Uploading...</span></>
              : <><Image size={28} className="mb-1" /><span className="text-sm font-medium">Click or drag image here</span><span className="text-xs">JPG, PNG, WebP · max 10 MB · auto-compressed</span></>
            }
          </div>
        )}
      </div>
      {error && <p className="text-red-500 text-xs mt-1">❌ {error}</p>}
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={e => handleFile(e.target.files[0])} />
    </div>
  );
};
export default ImageUpload;
